import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'

import moment from 'moment'

import {
  Grid,
  Dialog,
  Button,
  Divider,
  Checkbox,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'

import { Alert } from '@material-ui/lab'

import { makeStyles } from '@material-ui/core/styles'

import { getOrgs } from 'redux/actions/Org'

import { formatProducts, getCurrentPlanAndInvoiceData, handleShouldUpdateLicense } from './helpers'

import { StripeAPI } from '../../../lib/api/StripeAPI'
import { CheckCircle } from '@material-ui/icons'

import { freeProductPlacholder, productKeys } from './constants'

const API = {
  stripe: new StripeAPI()
}

const FREE_LICENSE = 'FREE'

const useStyles = makeStyles((theme) => ({
  page: {
    padding: `0 ${theme.shape.padding}px`
  },
  section: {
    marginBottom: theme.shape.padding * 2
  },
  sectionTitle: {
    fontSize: 25,
    fontWeight: 'bold'
  },
  sectionInfoContainer: {
    width: '100%',
    display: 'flex',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  sectionInfoCard: {
    width: '30%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.shape.padding,
    justifyContent: 'space-between',
    '& p': {
      margin: 0
    }
  },
  alertText: {
    margin: 0
  },
  rightBorder: {
    borderRight: `1px solid ${theme.palette.divider}`
  },
  sectionInfoCardTitle: {
    fontSize: 12
  },
  sectionInfoCardText: {
    fontWeight: 'bold'
  },
  manageInvoicesContainer: {
    display: 'flex',
    marginBottom: 16,
    alignItems: 'center',
    '& p': {
      margin: 0 
    }
  },
  manageButton: {
    marginLeft: theme.shape.padding
  },
  productQuantity: {
    marginBottom: theme.shape.padding
  },
  link: {
    fontSize: 14,
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  editCardText: {
    maxWidth: 150,
    cursor: 'pointer',
    color: theme.palette.primary.main,
    marginLeft: theme.shape.padding * 2,
  },
  productsContainer: {
    display: 'flex'
  },
  productSectionTitle: {
    fontSize: 20,
    marginBottom: 0,
    fontWeight: 'bold',
    marginLeft: theme.shape.padding
  },
  planOption: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.shape.padding,
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  namePriceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.shape.padding
  },
  billingPeriod: {
    fontWeight: 'bold'
  },
  price: {
    fontSize: 12,
    marginBottom: 0
  },
  userHelperText: {
    fontSize: 12,
    paddingTop: theme.shape.padding
  },
  checkContainer: {
    display: 'flex'
  },
  currentCheck: {
    marginRight: 5,
    color: theme.palette.success.main
  },
  listItems: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  listItem: {
    display: 'flex',
    fontSize: 14,
    '& p': {
      marginLeft: 10,
    }
  },
  certify: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      margin: 0,
      fontSize: 10,
      color: theme.palette.text.secondary
    }
  },
  dialogActions: {
    justifyContent: 'flex-start',
    padding: theme.shape.padding * 2,
    paddingTop: 0
  },
  stepContainer: {
    display: 'flex'
  },
  numberContainer: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: theme.shape.padding,
    backgroundColor: theme.palette.grey[600],
    textAlign: 'center',
    '& p': {
      margin: 0,
      color: theme.palette.common.white
    }
  },
  divider: {
    margin: `${theme.shape.padding}px 0`
  }
}))

const PlanOption = ({ plan = {}, selected, openCheckoutSession, isSuperadmin, handleStartTrial, isLoading = false }) => {
  const classes = useStyles()

  const isActive = () => {
    if(!selected)
      return !plan.priceId
    return plan.priceId === selected
  }

  return (
    <div className={classes.planOption} style={isActive() ? { border: `2px solid #3E7EF8` } : {}} datacy={'billingPlanOption'}>
      {(plan.billingPeriod && plan.amount) ? 
        <div className={classes.namePriceContainer}>
          <p className={classes.billingPeriod}>Billed {plan.billingPeriod}</p>
          <p className={classes.price}>{`$${plan.amount} per billable user`}</p>
        </div>
        :
        <div className={classes.namePriceContainer}>
          <p className={classes.price}>{plan.description}</p>
        </div>
      }
      <div className={classes.listItems}>
        {isActive() && !isLoading ?
          <div className={classes.checkContainer}>
            <CheckCircle className={classes.currentCheck}/>
            <p>Current</p>
          </div>
        :
          // disabled for 'direct', 'msp', 'reseller', and 'referral' segments
          (!plan.disableCreditCard || isSuperadmin) && <p className={classes.link} onClick={openCheckoutSession} datacy={'billingChangePlanBtn'}>{plan.productId ? 'Change to this plan' : null}</p>
        }
      </div>
      {isSuperadmin && plan.amount &&
        <div>
          <Button onClick={handleStartTrial} color={'primary'} variant={'outlined'}>Start Trial</Button>
        </div>
      }
    </div>
  )
}

const BillingPageView = props => {
  const classes = useStyles()
  
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [productQuantity, setProductQuantity] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [currentPlanData, setCurrentPlanData] = useState(null)
  const [numberOfTrialDays, setNumberOfTrialDays] = useState(0)
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)
  const [productQuantityMinimum, setProductQuantityMinimum] = useState(1)
  const [upgradeSubscriptionStep, setUpgradeSubscriptionStep] = useState(0)
  const [currentSelectedPricePlanId, setCurrentSelectedPricePlanId] = useState(null) /* eslint-disable-line */
  const [isStripeQuantityDialogOpen, setIsStripeQuantityDialogOpen] = useState(false)

  const getAndSetCustomerSubscription = async () => {
    setIsLoading(true)
    let activeCustomer = null

    if(!props.currentOrg)
      return

    if(props.location?.query?.session_id){
      // this user just finished a checkout session
      const checkoutSessionId = props.location.query.session_id

      // get checkout session to find customer id
      const checkoutSessionResponse = await API.stripe.getCheckoutSession(checkoutSessionId)
      if(!!checkoutSessionResponse.success && !!checkoutSessionResponse?.data?.session){
        const checkoutSession = checkoutSessionResponse.data.session
        const { data: { customer } } = await API.stripe.getCustomer(checkoutSession.customer)
        activeCustomer = customer
      }
    } else if(props?.currentOrg?.stripeCustomerId){
      // this user already has a customer id, use it
      const { data: { customer } } = await API.stripe.getCustomer(props.currentOrg.stripeCustomerId)
      activeCustomer = customer
    }

    const { data: { prices } } = await API.stripe.getPrices()
    
    // gets current data and formats for UI
    // planData = { productId, renewalDate, invoiceEmail, subscriptionId }
    // invoiceData = { nextIssueDate, invoiceTotal }
    const { planData, invoiceData } = getCurrentPlanAndInvoiceData(activeCustomer)
    
    // determine whether or not current org requires a licensing change
    // this logic will likely be moved elsewhere in future iterations but
    // meets scope/requirements for v1 of self-service
    const licenseToUpdate = handleShouldUpdateLicense(prices, planData.productId, props.currentOrg)

    if(licenseToUpdate && invoiceData.isPaid){
      props.currentOrg.set({ 
        license: licenseToUpdate,
        stripeCustomerId: activeCustomer.id
      })
      props.currentOrg.update()
      setUpgradeSubscriptionStep(1)
      setIsUpgradeDialogOpen(true)
    } else if(licenseToUpdate === FREE_LICENSE){
      props.currentOrg.set({ license: licenseToUpdate })
      props.currentOrg.update()
    }

    let relevantProducts = formatProducts(prices, planData.priceId, props.currentOrg)

    setProducts(relevantProducts)
    setCurrentPlanData(planData)
    setCurrentInvoiceData(invoiceData)
    setIsLoading(false)
  }

  useEffect(() => {
    getAndSetCustomerSubscription()
  }, [props.currentOrg]) /* eslint-disable-line */

  useEffect(() => {
    if(!props.currentOrg){
      setIsLoading(true)
      props.getOrgs()
    }
  }, [props.currentOrg]) /* eslint-disable-line */

  const handleChangePlan = async (selectedStripePriceId, isTrial = false) => {
    const newSelectedProduct = products.find(({ priceId }) => priceId === selectedStripePriceId)
    setSelectedProduct(newSelectedProduct)

    if(isTrial){
      // if this is a superadmin starting a trial for
      // a user, open dialog for trial day entry
      toggleStripeQuantityDialog()
    } else if(currentPlanData?.subscriptionId){
      // if we have a subscription id already, open modal
      // to handle the subscription as stripe's checkout
      // session does not handle subscription updates
      handleSetProductQuantityMinimum(newSelectedProduct)
      toggleUpgradeDialog()
    } else {
      // otherwise we can open the stripe-hosted
      // checkout session to establish intent
      toggleStripeQuantityDialog()
    }
  }

  const handleStripeCheckoutSession = async () => {
    let currentCustomerId
    
    if(props.currentOrg.stripeCustomerId){
      currentCustomerId = props.currentOrg.stripeCustomerId
    }

    const body = { 
      customer: currentCustomerId || null,
      priceId: selectedProduct.priceId,
      redirectUrl: window.location.href,
      userCount: productQuantity,
      trialDays: numberOfTrialDays
    }
    
    const { data: { session } } = await API.stripe.createCheckoutSession(body)

    // open stripe hosted checkout in same window
    window.location.href = session.url
  }

  const handleCreateTrialSubscription = async () => {
    let currentCustomerId
    
    if(props.currentOrg.stripeCustomerId)
      currentCustomerId = props.currentOrg.stripeCustomerId

    const body = { 
      trialDays: numberOfTrialDays,
      priceId: selectedProduct.priceId,
      customer: currentCustomerId || null
    }

    const { data: { customerId } } = await API.stripe.createSubscription(body)

    props.currentOrg.set({ 
      stripeCustomerId: customerId,
      license: selectedProduct.license
    })

    setIsLoading(true)
    setIsStripeQuantityDialogOpen(false)
    await props.currentOrg.update()
    setUpgradeSubscriptionStep(1)
    setIsUpgradeDialogOpen(true)
    setIsLoading(false)
  }

  const handleEditCustomer = async () => {
    // disable customer view if no customer to view
    if(!props.currentOrg.stripeCustomerId)
      return

    const body = { 
      redirectUrl: window.location.href,
      customer: props.currentOrg.stripeCustomerId
    }

    const { data } = await API.stripe.createCustomerSession(body)

    // open stripe hosted customer portal
    window.location.href = data.session.url
  }

  const handleUpgradeSubscription = async () => {
    // non-customers should never make it here
    // but better safe than sorry
    if(!props.currentOrg.stripeCustomerId)
      return

    const body = { 
      quantity: productQuantity,
      plan: selectedProduct.priceId,
      subscriptionId: currentPlanData.subscriptionId
    }

    await API.stripe.updateSubscription(body)

    getAndSetCustomerSubscription()
    setUpgradeSubscriptionStep(1)
  }
  
  const handleUpgradeSubscriptionStepTwo = () => {
    // on reload, remove query string from href that
    // is provided by the stripe checkout session
    window.location = window.location.href.split("?")[0]
    toggleUpgradeDialog()
    setProductQuantity(productQuantityMinimum)
  }

  const toggleUpgradeDialog = () => {
    setIsChecked(false)
    setUpgradeSubscriptionStep(0)
    setIsUpgradeDialogOpen(!isUpgradeDialogOpen)
  }

  const toggleStripeQuantityDialog = (selectedStripePriceId) => {
    const stripePriceId = selectedStripePriceId ? selectedStripePriceId : null
    setCurrentSelectedPricePlanId(stripePriceId)
    setIsStripeQuantityDialogOpen(!isStripeQuantityDialogOpen)
  }

  const toggleCheck = () => {
    setIsChecked(!isChecked)
  }

  const handleSetProductQuantityMinimum = plan => {
    // advanced licensing requires a
    // minimum of 50 purchased
    let minimum = plan.name.toLowerCase().includes('advanced') ? 50 : 1

    setProductQuantity(minimum)
    setProductQuantityMinimum(minimum)
  }

  const handleProductQuantityChange = e => { 
    setProductQuantity(e.target.value)
  }

  const handleNumberOfTrialDaysChange = e => {
    setNumberOfTrialDays(e.target.value)
  }

  return (
    <div className={classes.page}> 
    {currentPlanData && currentPlanData.isInTrial &&
      <Alert 
        severity={'info'}
        action={
          <Button color={'inherit'} size={'small'} onClick={handleEditCustomer}>
            MANAGE SUBSCRIPTION
          </Button>
        }
      >
          <p className={classes.alertText}>Your trial will end and you will be charged on {moment.unix(currentPlanData?.trialEndDate).format('MMMM DD YYYY')}</p>
      </Alert>
    }
      <div className={classes.section}>
        <p className={classes.sectionTitle}>Plans</p>
        {isLoading ?
            <PlanOption isLoading={true} plan={{ description: 'Loading plan data...' }} />
        :
          !!products.length && (
            <div>
              <div className={classes.productsContainer}>
                <Grid direction={'row'} container spacing={2}>
                  <Grid container direction={'column'} item xs={3} spacing={2}>
                    <p className={classes.productSectionTitle}>Free</p>
                    <Grid item style={{ height: '90%' }}>
                      <PlanOption 
                        plan={freeProductPlacholder} 
                        isSuperadmin={props.superadmin}
                        selected={currentPlanData ? currentPlanData.priceId : null} 
                      />
                    </Grid>
                  </Grid>
                  {productKeys.map((productKey, idx) => (
                    <Grid key={idx} container direction={'column'} item xs={3} spacing={2}>
                      <p className={classes.productSectionTitle}>{productKey}</p>
                      {products.filter(({ name }) => name.includes(productKey)).map((plan, index) => (
                        <Grid item key={index}>
                          <PlanOption  
                            plan={plan} 
                            isSuperadmin={props.superadmin}
                            openCheckoutSession={() => handleChangePlan(plan.priceId)} 
                            selected={currentPlanData ? currentPlanData.priceId : null} 
                            handleStartTrial={() => handleChangePlan(plan.priceId, true)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ))}
                </Grid>
              </div>
              <p className={classes.userHelperText}>A "billable user" refers to the number of knowledge workers at your organization; basically the number of employees with emails who use them.</p>
            </div>
          )
        }
      </div>

      <div className={classes.section}>
        <p className={classes.sectionTitle}>Renewal (monthly)</p>
        <div className={classes.sectionInfoContainer}>
          <div className={`${classes.sectionInfoCard} ${classes.rightBorder}`}>
            <p className={classes.sectionInfoCardTitle}>Plan will renew on:</p>
            <p className={classes.sectionInfoCardText}>
              {currentPlanData?.renewalDate ? moment.unix(currentPlanData.renewalDate).format('MMMM DD YYYY') : 'no billable plan on file'}
            </p>
            {/* <p className={classes.link}>Try the Professional Plan risk-free for 30 days</p> TODO: tie to...something */}
            <div/>
          </div>
          <div className={`${classes.sectionInfoCard} ${classes.rightBorder}`}>
            <p className={classes.sectionInfoCardTitle}>Billable users included:</p>
            <p className={classes.sectionInfoCardText}>Unlimited</p>
            <div/>
          </div>
          <div className={classes.sectionInfoCard}>
            <p className={classes.sectionInfoCardTitle}>Send invoices to:</p>
            <p className={classes.sectionInfoCardText}>
              {currentPlanData?.invoiceEmail ? currentPlanData.invoiceEmail : 'no billable plan on file'}
            </p>
            <p className={classes.link} onClick={handleEditCustomer}>Change contact</p>
          </div>
        </div>
      </div>

      {!!currentInvoiceData && <div className={classes.section}>
        <div className={classes.manageInvoicesContainer}>
          <p className={classes.sectionTitle}>Invoices</p>
          <Button className={classes.manageButton} onClick={handleEditCustomer} color={'primary'} variant={'outlined'}>Manage</Button>
        </div>
        <div className={classes.sectionInfoContainer}>
          <div className={`${classes.sectionInfoCard} ${classes.rightBorder}`}>
            <p className={classes.sectionInfoCardTitle}> Next invoice will be issued on:</p>
            <p className={classes.sectionInfoCardText}>
              {currentInvoiceData?.nextIssueDate ? moment.unix(currentInvoiceData.nextIssueDate).format('MMMM DD YYYY') : 'no invoice on file'}
            </p>
          </div>
          <div className={`${classes.sectionInfoCard} ${classes.rightBorder}`}>
            <p className={classes.sectionInfoCardTitle}>Billable users detected this invoice period:</p>
            <p className={classes.sectionInfoCardText}>{currentInvoiceData?.knowledgeWorkers ? currentInvoiceData?.knowledgeWorkers : 'no invoice on file'}</p>
          </div>
          <div className={classes.sectionInfoCard}>
            <p className={classes.sectionInfoCardTitle}>Invoice total:</p>
            <p className={classes.sectionInfoCardText}>
              {currentInvoiceData?.invoiceTotal ? currentInvoiceData.invoiceTotal : 'no invoice on file'}
            </p>
          </div>
        </div>
      </div>}

      <Dialog open={isUpgradeDialogOpen} onClose={toggleUpgradeDialog}>
        <DialogTitle>Upgrade{upgradeSubscriptionStep === 1 && 'd'} {selectedProduct ? 'to' : null} {selectedProduct?.name || ''}</DialogTitle>
        {upgradeSubscriptionStep === 0 ?
          <DialogContent>
            <p>By checking the box and selecting to 'Upgrade Now' you are giving Blumira permission to upgrade your plan and incur any related charges to the credit card stored in Stripe.</p>
            <p>To view your saved credit card details, select 'Manage Credit Card' to open your Stripe customer portal.</p>
            <p>After upgrading you may need to <strong>refresh your browser to experience relevant changes to your account</strong>.</p>
            <TextField
              type={'number'}
              value={productQuantity}
              className={classes.productQuantity}
              label={'# of users'}
              onChange={handleProductQuantityChange}
              error={productQuantity < productQuantityMinimum}
              helperText={
                productQuantity < productQuantityMinimum ? `You must purchase at least ${productQuantityMinimum} license${productQuantityMinimum > 1 ? 's' : ''}` 
                : ''
              }
            />
            <div className={classes.certify}>
              <Checkbox
                color={'primary'}
                checked={isChecked}
                onChange={toggleCheck}
              />
              <p>I agree to upgrading my plan and to recieve a prorated invoice immediately, charged to the credit card on file</p>
            </div>
          </DialogContent>
        :
          <DialogContent>
            <p>Your plan was successfully upgraded.</p>
            <p>Please refresh your browser for your new experience.</p>
          </DialogContent>
        }
        {upgradeSubscriptionStep === 0 ?
          <DialogActions className={classes.dialogActions}>
            <Button disabled={!isChecked} onClick={handleUpgradeSubscription} color={'primary'} variant={'contained'}>
              Upgrade now
            </Button>
            <Button onClick={handleEditCustomer} color={'primary'} variant={'contained'}>
              Manage credit card
            </Button>
            <Button onClick={toggleUpgradeDialog} color={'primary'} variant={'outlined'}>
              Cancel
            </Button>
          </DialogActions>
        : 
          <DialogActions className={classes.dialogActions}>
            <Button onClick={handleUpgradeSubscriptionStepTwo} color={'primary'} variant={'contained'}>
              Refresh Browser
            </Button>
            <Button onClick={toggleUpgradeDialog} color={'primary'} variant={'outlined'}>
              Cancel
            </Button>
          </DialogActions>
        } 
      </Dialog>

      <Dialog open={isStripeQuantityDialogOpen} onClose={toggleStripeQuantityDialog} datacy={'billingChangePlanDialog'}>
        <DialogTitle>Change to {selectedProduct?.name || ''}</DialogTitle>
        <DialogContent>
          {props.superadmin && 
            <div>
              <p>Blumira super admins may begin trials for customers.</p>
              <div className={classes.stepContainer}>
                <div className={classes.numberContainer}><p>1</p></div>
                <p>Enter number of trial days below</p>
              </div>
              <div className={classes.stepContainer}>
                <div className={classes.numberContainer}><p>2</p></div>
                <p>Click 'Create Trial Subscription'</p>
              </div>
              <div className={classes.stepContainer}>
                <div className={classes.numberContainer}><p>3</p></div>
                <p>Refresh the browser to see your updates</p>
              </div>
              <div className={classes.stepContainer}>
                <div className={classes.numberContainer}><p>4</p></div>
                <p>To cancel, select 'Manage Subscription' in the alert then in the Stripe portal 'Cancel Plan'</p>
              </div>
              <TextField
                type={'number'}
                value={numberOfTrialDays}
                label={'# of trial days'}
                onChange={handleNumberOfTrialDaysChange}
                helperText={'free days added to beginning of subscription'}
              />
              <Divider className={classes.divider} />
            </div>
          }
          <p>Blumira uses the secure Stripe Portal to manage subscriptions.</p>
          <div className={classes.stepContainer}>
            <div className={classes.numberContainer}><p>1</p></div>
            <p>Click Launch Stripe Portal below</p>
          </div>
          <div className={classes.stepContainer}>
            <div className={classes.numberContainer}><p>2</p></div>
            <p>Enter your total user count when prompted to 'Update Quantity'</p>
          </div>
          <div className={classes.stepContainer}>
            <div className={classes.numberContainer}><p>3</p></div>
            <p>Enter payment information, then click 'Subscribe'</p>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {props.superadmin &&
            <Button onClick={handleCreateTrialSubscription} color={'primary'} variant={'contained'}>
              Create Trial Subscription
            </Button>
          }
          <Button onClick={handleStripeCheckoutSession} color={'primary'} variant={'contained'}>
            Launch Stripe Portal
          </Button>
          <Button onClick={toggleStripeQuantityDialog} color={'primary'} variant={'outlined'}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => {
  const orgId = state.location.payload.orgId

  const currentOrg = state.org.listOrgs.orgs.find(o => o['id'] === orgId)

  return { 
    orgId,
    currentOrg,
    location: state.location,
    superadmin: state?.session?.settings?.user?.superadmin
  }
}

const mapDispatchToProps = dispatch => ({
  getOrgs: () => dispatch(getOrgs())
})

export default connect(mapStateToProps, mapDispatchToProps)(BillingPageView)
